import { Controller } from "@hotwired/stimulus"
import { parseUserInput, formatNumberForDisplay } from "../helpers/number_formatting"

export default class extends Controller {
  static targets = [
    "discountExclTax",
    "discountInclTax",
    "unitPrice",
    "taxCode",
    "quantity",
    "totalExclTax",
    "totalInclTax",
    "offerItem",
    "toggleButton",
    "discountExclTaxWrapper",
    "discountInclTaxWrapper",
    "offerInfo",
    "offerInput"
  ]

  static values = {
    compoundedTaxRates: Array,
    taxRates: Object,
    discountableType: String,
    discountableId: Number
  }

  connect() {
    this.initDiscountFieldsState()
    this.removeErrors()
    this.formatExistingDiscounts()
  }

  formatExistingDiscounts() {
    this.discountExclTaxTarget.value = formatNumberForDisplay(parseUserInput(this.discountExclTaxTarget.value))
    this.discountInclTaxTarget.value = formatNumberForDisplay(parseUserInput(this.discountInclTaxTarget.value))
  }

  removeErrors() {
    const feedbackElements = document.querySelectorAll(".invalid-feedback");
    feedbackElements.forEach((element) => {
      element.style.display = "none";
    });
  }

  initDiscountFieldsState() {
    this.applyFieldRules()
  }

  applyFieldRules() {
    this.toggleFieldState(this.discountExclTaxTarget, this.discountExclTaxWrapperTarget, this.unitPriceFormat === "ttc")
    this.toggleFieldState(this.discountInclTaxTarget, this.discountInclTaxWrapperTarget, this.unitPriceFormat === "ht")
  }

  // Vérifie et met à jour les discounts dynamiquement
  checkAndUpdateDiscounts() {
    if (this.areFieldsValid) {
      this.copyTotalsToDiscounts()
    } else {
      this.cleanDiscountFields()
    }
    this.calculateDiscounts()
  }

  handleOfferInfoMessage() {
    if (this.nonValuedOffered()) {
      this.displayOfferInfo()
      this.unitPriceTarget.classList.add("border-info", "shadow-sm")
      analytics.track("Quote zero-cost item", {
        discountable_type: this.discountableTypeValue,
        discountable_id: this.discountableIdValue
      })
    } else {
      this.hideOfferInfo()
      this.unitPriceTarget.classList.remove("border-info", "shadow-sm")
    }
  }

  toggleOfferItem() {
    if (this.offerItemTarget.checked) {
      this.copyTotalsToDiscounts() // Met à jour les montants
      this.disableDiscountFields() // Désactive les champs
      this.calculateDiscounts()
    } else {
      this.resetFields()
    }
  }

  toggleOfferInputField() {
    if(this.offerInputTarget) {
      this.offerInputTarget.value = this.offerItemTarget.checked
    }
  }

  // Réinitialise les champs de remise
  resetFields() {
    this.cleanDiscountFields()
    this.enableDiscountFields()
    this.initDiscountFieldsState()
  }

  // Vide les champs de remise
  cleanDiscountFields() {
    this.discountExclTaxTarget.value = ""
    this.discountInclTaxTarget.value = ""
  }

  // Désactive les champs de remise
  disableDiscountFields() {
    this.toggleFieldState(this.discountExclTaxTarget, this.discountExclTaxWrapperTarget, true)
    this.toggleFieldState(this.discountInclTaxTarget, this.discountInclTaxWrapperTarget, true)
  }

  // Active les champs de remise
  enableDiscountFields() {
    this.toggleFieldState(this.discountExclTaxTarget, this.discountExclTaxWrapperTarget, false)
    this.toggleFieldState(this.discountInclTaxTarget, this.discountInclTaxWrapperTarget, false)
  }

  // Active/Désactive un champ avec style et état en lecture seule
  toggleFieldState(target, wrapperTarget, isDisabled) {
    if (this.isOffered()) {
      this.offerItemTarget.checked = true
      isDisabled = true
    }
    const action = isDisabled ? "add" : "remove"
    target.readOnly = isDisabled
    wrapperTarget.classList[action]("input-suffix__200")
    target.classList[action]("quiet")
  }

  isOffered() {
    const hasValue = (el) => el && el.value != null && el.value.toString().trim() !== ""
    if (!hasValue(this.totalExclTaxTarget) || !hasValue(this.totalInclTaxTarget)) return false

    const totalExcl = parseUserInput(this.totalExclTaxTarget.value)
    const totalIncl = parseUserInput(this.totalInclTaxTarget.value)

    if (totalExcl <= 0 || totalIncl <= 0) return false

    const discountExcl = parseUserInput(this.discountExclTaxTarget.value)
    const discountIncl = parseUserInput(this.discountInclTaxTarget.value)

    return discountExcl === totalExcl && discountIncl === totalIncl
  }

  displayOfferInfo() {
    this.offerInfoTarget.classList.remove("d-none")
  }

  hideOfferInfo() {
    this.offerInfoTarget.classList.add("d-none")
  }

  nonValuedOffered() {
    return (
      (this.unitPriceTarget.value != "") && (parseUserInput(this.unitPriceTarget.value) == 0)
    )
  }

  // Copie les totaux dans les discounts
  copyTotalsToDiscounts() {
    if (this.offerItemTarget.checked) {
      this.discountExclTaxTarget.value = formatNumberForDisplay(this.totalExcTaxValue)
      this.discountInclTaxTarget.value = formatNumberForDisplay(this.totalInclTaxValue)
    }
  }

  // Calcul des remises
  calculateDiscounts() {
    let taxRate = this.taxRate

    if (this.compoundedTaxRatesValue.includes(this.taxCodeValue)) {
      taxRate = this.calculateCompoundedVATRate(this.totalExcTaxValue, this.totalInclTaxValue)
    }

    if (this.unitPriceFormat === "ht") {
      if (!this.discountExclTaxTarget.value) {
        this.discountInclTaxTarget.value = null
        return
      }
      const discountExclTax = parseUserInput(this.discountExclTaxTarget.value) || 0
      const discountInclTax = discountExclTax * (1 + taxRate)
      this.discountInclTaxTarget.value = formatNumberForDisplay(discountInclTax)
    } else {
      if (!this.discountInclTaxTarget.value) {
        this.discountExclTaxTarget.value = null
        return
      }
      const discountInclTax = parseUserInput(this.discountInclTaxTarget.value) || 0
      const discountExclTax = discountInclTax / (1 + taxRate)
      this.discountExclTaxTarget.value = formatNumberForDisplay(discountExclTax)
    }
  }

  calculateCompoundedVATRate(total_ht, total_ttc) {
    return +((total_ttc - total_ht) / total_ht)
  }

  get unitPriceFormat() {
    return this.data.get("unitPriceFormat")
  }

  get taxCodeValue() {
    return this.taxCodeTarget.value.trim()
  }

  get taxRate() {
    return +this.taxRatesValue[this.taxCodeValue]
  }

  get hasDiscount() {
    return (
      (this.discountExclTaxTarget.value && parseUserInput(this.discountExclTaxTarget.value) > 0) ||
      (this.discountInclTaxTarget.value && parseUserInput(this.discountInclTaxTarget.value) > 0)
    )
  }

  get unitPriceValue() {
    return parseUserInput(this.unitPriceTarget.value.trim()) || 0
  }

  get quantityValue() {
    return parseUserInput(this.quantityTarget.value.trim()) || 0
  }

  get totalExcTaxValue() {
    return parseUserInput(this.totalExclTaxTarget.value.trim()) || 0
  }

  get totalInclTaxValue() {
    return parseUserInput(this.totalInclTaxTarget.value.trim()) || 0
  }

  get areFieldsValid() {
    return this.unitPriceValue > 0 &&
      this.taxCodeValue !== "" &&
      this.quantityValue > 0 &&
      this.totalExcTaxValue > 0 &&
      this.totalInclTaxValue > 0
  }
}
